/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Component, effect, ElementRef, inject, Input, output, OutputEmitterRef, signal, WritableSignal } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';

export const tabButtonVariants = cva(
  'relative w-full px-6 py-4 outline-offset-2 focus-visible:outline-focus focus-visible:outline-2 cursor-pointer first-of-type:rounded-t-[5px] last-of-type:rounded-b-[5px] focus-visible:z-1 overflow-hidden text-text',
  {
    variants: {
      variant: {
        primary: 'hover:bg-tab-hover',
      },
      active: {
        false: null,
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
    compoundVariants: [
      {
        variant: 'primary',
        active: true,
        class:
          'bg-tab-active before:content-[""] before:absolute before:top-0 before:bottom-0 before:left-0 before:w-[3px] before:bg-focus',
      },
    ],
  },
);
export type TabButtonVariants = VariantProps<typeof tabButtonVariants>;

@Component({
  selector: 'ods-tab-button',
  template: `<ng-content />`,
  host: {
    role: 'tab',
    '[class]': 'tabButtonVariants({ variant: this.variant, active: this.active() })',
    '[id]': 'id()',
    '[attr.tabindex]': 'active() ? 0 : -1',
    '[attr.aria-controls]': 'panelId()',
    '[attr.aria-selected]': 'active()',
    '(click)': 'handleClick()',
  },
})
export class TabButtonComponent {
  @Input() variant: TabButtonVariants['variant'];

  public ref: ElementRef<HTMLElement> = inject(ElementRef);

  public id: WritableSignal<string> = signal('');
  public panelId: WritableSignal<string> = signal('');
  public active: WritableSignal<boolean> = signal(false);
  public readonly clicked: OutputEmitterRef<void> = output<void>();
  public onClick: () => void = () => {};
  public readonly tabButtonVariants = tabButtonVariants;

  constructor() {
    effect(() => {
      if (this.active()) {
        this.ref.nativeElement.focus();
      }
    });
  }

  public handleClick(): void {
    this.onClick();
    this.clicked.emit();
  }
}
